import { useState, type FormEvent, type ReactNode } from 'react'
import { Eye, EyeOff, KeyRound, Link2, ShieldCheck } from 'lucide-react'
import logo from '@/assets/TradeOne.png'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { credentialsSaved } from '@/features/connection/connectionSlice'
import { Button } from '@/components/ui/Button'
import { Field, TextInput } from '@/components/ui/Field'

/**
 * The console has no user accounts. Until an operator supplies the server URL
 * and the shared admin key there is nothing to show, so this stands in place of
 * a sign-in screen.
 */
export function ConnectScreen(): ReactNode {
  const dispatch = useAppDispatch()
  const stored = useAppSelector((s) => s.connection)
  const [cloudUrl, setCloudUrl] = useState(stored.cloudUrl)
  const [adminKey, setAdminKey] = useState(stored.adminKey)
  const [reveal, setReveal] = useState(false)

  const canSubmit = cloudUrl.trim().length > 0 && adminKey.trim().length > 0

  const submit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    dispatch(credentialsSaved({ cloudUrl, adminKey }))
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={logo} alt="" aria-hidden className="mb-3 h-12 w-auto" />
          <h1 className="text-[19px] font-bold tracking-tight text-fg">Trade One Admin</h1>
          <p className="label-caps mt-1 text-accent">Dealer operations</p>
          <p className="mt-3 text-[12.5px] text-muted">
            Point the portal at your sign-in server to begin.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-lg border border-line bg-surface p-6">
          <Field label="Cloud server URL" hint="The API base, ending in /api/v2.">
            {(id) => (
              <TextInput
                id={id}
                icon={Link2}
                value={cloudUrl}
                onChange={(e) => setCloudUrl(e.target.value)}
                placeholder="https://your-server.example.com/api/v2"
                autoComplete="off"
                spellCheck={false}
              />
            )}
          </Field>

          <Field label="Admin key" hint="Must match ADMIN_API_KEY in the server environment.">
            {(id) => (
              <TextInput
                id={id}
                icon={KeyRound}
                type={reveal ? 'text' : 'password'}
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Shared admin key"
                autoComplete="off"
                spellCheck={false}
                trailing={
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={reveal ? EyeOff : Eye}
                    onClick={() => setReveal((v) => !v)}
                    aria-label={reveal ? 'Hide key' : 'Show key'}
                  />
                }
              />
            )}
          </Field>

          <Button
            type="submit"
            variant="primary"
            icon={ShieldCheck}
            className="w-full"
            disabled={!canSubmit}
          >
            Connect
          </Button>

          <p className="text-center text-[11px] leading-relaxed text-subtle">
            The key grants full admin rights and is stored only in this browser.
          </p>
        </form>
      </div>
    </div>
  )
}
